/**
 * PGSS-5309 — when to show the discrepancy banner.
 *
 * A resolved payload from Sync is still a truthy object (`has_discrepancy: false`).
 * Do not use bare truthiness on discrepancy; hide only when there is no mismatch
 * and no recorded decision to display.
 */
export const discrepancyForDisplay = (disc) =>
  disc && !disc.has_discrepancy && !disc.latest_decision ? null : (disc ?? null);

const SYNC_LEFTOVER_LABELS = {
  product: 'Product',
  vas: 'VAS',
  surcharge: 'Surcharge',
  shipping: 'Shipping',
};

// Why Sync leaves each part alone, in the user's terms — the next step, not the mechanism.
const SYNC_LEFTOVER_HINTS = {
  product: 'a product may be billed on another invoice for this order',
  vas: "the VAS may be billed on another invoice, or the order's VAS items don't add up to its VAS price",
  surcharge: 'the surcharge could not be matched automatically',
  shipping: 'this button does not change shipping — edit it on the invoice',
};

const money = (value) => Number(value ?? 0).toFixed(2);

/**
 * PGSS-5612 — what to tell the user after "Update invoice to match order".
 *
 * Sync always reported success, even when the invoice still disagreed with the order (invoice 2522
 * said saved with its VAS still missing). The response carries the comparison re-run after the sync,
 * so say plainly what still differs instead.
 *
 * @returns {{ message: string, variant: 'success' | 'warning' }}
 */
export const syncResultMessage = (discrepancy) => {
  const leftovers = (discrepancy?.components || []).filter((row) => row.mismatched);
  if (!discrepancy?.has_discrepancy || leftovers.length === 0) {
    return { message: 'Invoice updated to match the order', variant: 'success' };
  }
  const parts = leftovers.map((row) => {
    const label = SYNC_LEFTOVER_LABELS[row.component] || row.component;
    const order = row.order_no || row.order_id;
    return `${label} on order ${order}: invoice ${money(row.invoice_amount)}, order ${money(row.order_amount)}`;
  });
  const hints = [...new Set(leftovers.map((row) => SYNC_LEFTOVER_HINTS[row.component]).filter(Boolean))];
  const count = leftovers.length === 1 ? 'one part still differs' : `${leftovers.length} parts still differ`;
  const pronoun = leftovers.length === 1 ? 'it' : 'them';
  return {
    message: `Invoice updated, but ${count} from the order — ${parts.join('; ')}. Sync could not change ${pronoun}: ${hints.join('; ')}.`,
    variant: 'warning',
  };
};
